import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Chip,
  Divider,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { OpenInNew as OpenInSpotifyIcon } from '@mui/icons-material';
import { Playlist } from '../../models';

interface PlaylistCreatedProps {
  playlist: Playlist & {
    tracksFound?: number;
    tracksNotFound?: string[];
    totalTracksRequested?: number;
  };
  onViewDetails: (playlistId: string) => void;
}

const PlaylistCreated: React.FC<PlaylistCreatedProps> = ({ playlist, onViewDetails }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>Playlist Created</Typography>
      <Card>
        <CardContent>
          <Typography variant="h5">{playlist.name}</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {playlist.description || 'No description'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              {playlist.totalTracks} tracks • Created by {playlist.owner.displayName}
            </Typography>
            <Tooltip title="Open in Spotify">
              <IconButton 
                size="small"
                onClick={() => window.open(`https://open.spotify.com/playlist/${playlist.id}`, '_blank')}
              >
                <OpenInSpotifyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Display track search results if available */}
          {playlist.tracksFound !== undefined && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip 
                  label={`${playlist.tracksFound} of ${playlist.totalTracksRequested} tracks found`} 
                  color={playlist.tracksFound > 0 ? "success" : "warning"}
                  size="small"
                  sx={{ mr: 1 }}
                />
              </Box>
              
              {/* Show not found tracks if any */}
              {playlist.tracksNotFound && playlist.tracksNotFound.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    The following tracks couldn't be found:
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {playlist.tracksNotFound.map((track, index) => (
                        <Box key={index} component="span" sx={{ display: 'block' }}>
                          • {track}
                        </Box>
                      ))}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            onClick={() => onViewDetails(playlist.id)}
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default PlaylistCreated;
